'use client';

import { atom, selector } from 'recoil';

import {
  hasSelectedBodyTypesSelector,
  selectedBodyTypesSelector,
} from '@/states/filters/body-type.state';
import {
  brandsRangesAllSelectedSelector,
  brandsRangesSelectedFiltersSelector,
} from '@/states/filters/brands-ranges.state';
import {
  hasSelectedColorFiltersSelector,
  selectedColorFiltersSelector,
} from '@/states/filters/colors.state';
import {
  hasSelectedDoorsFilterSelector,
  selectedDoorsFilterSelector,
} from '@/states/filters/doors.state';
import {
  hasSelectedDrivetrainFilterSelector,
  selectedDrivetrainTypesSelector,
} from '@/states/filters/drivetrain.state';
import {
  hasSelectedFeaturesFilterSelector,
  selectedFeaturesSelector,
} from '@/states/filters/features.state';
import {
  hasSelectedConsumptionFilterSelector,
  selectedConsumptionFilterSelector,
} from '@/states/filters/fuel-consumption.state';
import {
  hasSelectedFuelTypesFilterSelector,
  selectedFuelTypesSelector,
} from '@/states/filters/fuel-type.state';
import {
  hasSelectedMileageFilterSelector,
  selectedMileageFilterSelector,
} from '@/states/filters/mileage.state';
import {
  hasSelectedPriceFilterSelector,
  selectedPriceFilterSelector,
} from '@/states/filters/price.state';
import {
  hasSelectedTransmissionFilterSelector,
  selectedTransmissionTypesSelector,
} from '@/states/filters/transmission-type.state';
import {
  hasSelectedYearsFilterSelector,
  selectedYearsFilterSelector,
} from '@/states/filters/years.state';
import { ChildFilter, ParentFilter } from '@/types/filters/filters.types';
import {
  BodyTypesQuery,
  BrandsRangesQuery,
  ColorsQuery,
  ConsumptionQuery,
  DoorsQuery,
  DrivetrainQuery,
  FeaturesQuery,
  FuelTypesQuery,
  MileageQuery,
  PriceQuery,
  ProvincesDealershipsQuery,
  TransmissionQuery,
  UserCarsFilterQuery,
  VehicleType,
  YearsQuery,
} from '@/types/used-cars/used-cars.types';

import {
  provincesDealershipsAllSelectedSelector,
  provincesDealershipsSelectedFiltersSelector,
  saleDateFilterSelector,
} from './provinces-dealerships.state';

export const carTypeState = atom<VehicleType | null>({
  key: 'carTypeId',
  default: null,
});

const brandsRangesQuery = selector<BrandsRangesQuery>({
  key: 'brandsRangesQuery',
  get({ get }) {
    const { brandsRangesAllSelected } = get(brandsRangesAllSelectedSelector);

    if (brandsRangesAllSelected) return {};

    const { brandsRangesSelectedFilters } = get(
      brandsRangesSelectedFiltersSelector,
    );

    const q = { _or: [] as any };

    brandsRangesSelectedFilters.forEach((filter) => {
      if (filter.children?.length) {
        q._or.push({
          _and: [
            {
              brand_id: { id: { _in: [filter.id] } },
            },
            {
              range_id: {
                id: {
                  _in: filter.children.map(
                    ({ id }) => id.toString().split('-')[1],
                  ),
                },
              },
            },
          ],
        });
      } else {
        q._or.push({
          brand_id: { id: { _in: [filter.id] } },
        });
      }
    });

    return q._or.length ? q : {};
  },
});

const provincesDealershipsQuery = selector<ProvincesDealershipsQuery>({
  key: 'provincesDealershipsQuery',
  get({ get }) {
    const { provincesDealershipsAllSelected } = get(
      provincesDealershipsAllSelectedSelector,
    );

    if (provincesDealershipsAllSelected) return {};

    const { provincesDealershipsSelectedFilters } = get(
      provincesDealershipsSelectedFiltersSelector,
    );

    const dealerships: any[] = [];
    const query = { dealership_id: { id: {} } };

    provincesDealershipsSelectedFilters.forEach(
      (filter: ParentFilter | ChildFilter) => {
        const province = filter as ParentFilter;

        if (province.children) {
          province.children.forEach((dealership: ChildFilter) => {
            dealerships.push(dealership.id);
          });
        } else {
          dealerships.push(filter.id);
        }
      },
    );

    if (dealerships.length) {
      query.dealership_id.id = { _in: dealerships };
      return query;
    }

    return {};
  },
});

const yearArrayRange = (start: number, end: number): number[] => {
  const arrayRange = [];

  for (let i = start; i <= end; i++) {
    arrayRange.push(i);
  }

  return arrayRange;
};

const yearsQuery = selector<YearsQuery>({
  key: 'yearsQuery',
  get({ get }) {
    const hasSelectedYearsFilter = get(hasSelectedYearsFilterSelector);
    const yearsFilter = get(selectedYearsFilterSelector);

    if (hasSelectedYearsFilter) {
      const minYear = yearsFilter.min;
      const maxYear = yearsFilter.max;
      const inYear = maxYear - minYear;
      const range = yearArrayRange(1, inYear);
      const arrayOfYear = [minYear];

      range.forEach((value) => {
        arrayOfYear.push(minYear + value);
      });

      return {
        year: {
          _in: arrayOfYear,
        },
      };
    }

    return {};
  },
});

const mileageQuery = selector<MileageQuery>({
  key: 'mileageQuery',
  get({ get }) {
    const hasSelectedMileageFilter = get(hasSelectedMileageFilterSelector);
    const mileageFilter = get(selectedMileageFilterSelector);

    if (hasSelectedMileageFilter) {
      return {
        mileage: {
          _between: Object.values(mileageFilter).toString(),
        },
      };
    }

    return {};
  },
});

const priceQuery = selector<PriceQuery>({
  key: 'priceQuery',
  get({ get }) {
    const hasSelectedPriceFilter = get(hasSelectedPriceFilterSelector);
    const priceFilter = get(selectedPriceFilterSelector);

    if (hasSelectedPriceFilter) {
      return {
        sales_price_including_vat: {
          _between: Object.values(priceFilter).toString(),
        },
      };
    }

    return {};
  },
});

const doorsQuery = selector<DoorsQuery>({
  key: 'doorsQuery',
  get({ get }) {
    const hasSelectedDoorsFilter = get(hasSelectedDoorsFilterSelector);
    const doorsFilter = get(selectedDoorsFilterSelector);

    if (hasSelectedDoorsFilter) {
      return {
        spec_doors: {
          id: { _in: doorsFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const bodyTypesQuery = selector<BodyTypesQuery>({
  key: 'bodyTypesQuery',
  get({ get }) {
    const hasSelectedBodyTypes = get(hasSelectedBodyTypesSelector);
    const bodyTypesFilter = get(selectedBodyTypesSelector);

    if (hasSelectedBodyTypes) {
      return {
        spec_body_type: {
          id: { _in: bodyTypesFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const colorsQuery = selector<ColorsQuery>({
  key: 'colorsQuery',
  get({ get }) {
    const hasSelectedColor = get(hasSelectedColorFiltersSelector);
    const colorsFilter = get(selectedColorFiltersSelector);

    if (hasSelectedColor) {
      return {
        spec_body_colors: {
          id: { _in: colorsFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const featuresQuery = selector<FeaturesQuery>({
  key: 'featuresQuery',
  get({ get }) {
    const hasSelectedFeature = get(hasSelectedFeaturesFilterSelector);
    const featuresFilter = get(selectedFeaturesSelector);

    if (hasSelectedFeature) {
      return {
        _and: featuresFilter.map((filter) => {
          return {
            spec_features: {
              spec_features_id: { _eq: filter?.id },
            },
          };
        }),
      } as any;
    }

    return {};
  },
});

const fuelTypesQuery = selector<FuelTypesQuery>({
  key: 'fuelTypesQuery',
  get({ get }) {
    const hasSelectedFuelType = get(hasSelectedFuelTypesFilterSelector);
    const fuelTypesFilter = get(selectedFuelTypesSelector);

    if (hasSelectedFuelType) {
      return {
        spec_fuel_type: {
          id: { _in: fuelTypesFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const transmissionQuery = selector<TransmissionQuery>({
  key: 'transmissionQuery',
  get({ get }) {
    const hasSelectedTransmission = get(hasSelectedTransmissionFilterSelector);
    const transmissionFilter = get(selectedTransmissionTypesSelector);

    if (hasSelectedTransmission) {
      return {
        spec_transmission: {
          id: { _in: transmissionFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const drivetrainQuery = selector<DrivetrainQuery>({
  key: 'drivetrainQuery',
  get({ get }) {
    const hasSelectedDrivetrain = get(hasSelectedDrivetrainFilterSelector);
    const drivetrainFilter = get(selectedDrivetrainTypesSelector);

    if (hasSelectedDrivetrain) {
      return {
        spec_drivetrain: {
          id: { _in: drivetrainFilter.map((filter) => filter.id) },
        },
      };
    }

    return {};
  },
});

const consumptionQuery = selector<ConsumptionQuery>({
  key: 'consumptionQuery',
  get({ get }) {
    const hasSelectedConsumptionFilter = get(
      hasSelectedConsumptionFilterSelector,
    );
    const consumptionFilter = get(selectedConsumptionFilterSelector);

    if (hasSelectedConsumptionFilter) {
      return {
        spec_litres_100km: {
          float_value: {
            _between: Object.values(consumptionFilter).toString(),
          },
        },
      };
    }

    return {};
  },
});

export const filtersQuerySelector = selector<UserCarsFilterQuery>({
  key: 'filtersQuery',
  get: ({ get }) => {
    const brandsRanges = get(brandsRangesQuery);
    const years = get(yearsQuery);
    const mileage = get(mileageQuery);
    const price = get(priceQuery);
    const doors = get(doorsQuery);
    const bodyTypes = get(bodyTypesQuery);
    const colors = get(colorsQuery);
    const features = get(featuresQuery);
    const fuelTypes = get(fuelTypesQuery);
    const transmissions = get(transmissionQuery);
    const drivetrain = get(drivetrainQuery);
    const provincesDealerships = get(provincesDealershipsQuery);
    const consumption = get(consumptionQuery);
    const saleDate = get(saleDateFilterSelector);

    const query = {
      ...brandsRanges,
      ...years,
      ...mileage,
      ...price,
      ...doors,
      ...bodyTypes,
      ...colors,
      ...fuelTypes,
      ...transmissions,
      ...drivetrain,
      ...features,
      ...provincesDealerships,
      ...consumption,
      ...saleDate,
    };

    return query as any;
  },
});

export const hasFiltersQuerySelector = selector<boolean>({
  key: 'hasFiltersQuerySelector',
  get({ get }) {
    const filtersQuery = get(filtersQuerySelector);

    return Boolean(Object.keys(filtersQuery).length);
  },
});

export const mustUseNewFiltersState = atom<boolean>({
  key: 'mustUseNewFilters',
  default: false,
});

export const isInitialQueryState = atom<boolean>({
  key: 'isInitialQuery',
  default: true,
});

export const isClearedState = atom<boolean>({
  key: 'isClearedState',
  default: false,
});

export const isSearchOpenState = atom<boolean>({
  key: 'isSearchOpe',
  default: false,
});
